import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../Core/RestHelper';
import AWSSetupModal from '../Components/AwsConnection';
import { AzureSetupModal } from '../Components/AzureSetupModal';

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserDashboard {
  external_id: string;
  has_connection_access?: boolean;
}

interface AwsAccountResult {
  role_arn: string;
  region: string;
  account_id: string;
  success: boolean;
  message: string;
  missing_permissions: string[];
  remarks: string[];
  trust_errors?: string[];
}

interface AwsVerifyResponse {
  success: boolean;
  message: string;
  accounts: AwsAccountResult[];
  verified_at: string;
}

interface AzureAccountResult {
  tenant_id: string;
  subscription_id: string;
  region: string;
  success: boolean;
  message: string;
  granted_roles: string[];
  missing_permissions: string[];
  remarks: string[];
}

interface AzureVerifyResponse {
  success: boolean;
  message: string;
  accounts: AzureAccountResult[];
  verified_at: string;
}

type ModalType = 'aws' | 'azure' | null;

// ── Constants ─────────────────────────────────────────────────────────────────

const NO_ACCESS_TOOLTIP = "You don't have permission to add connections. Contact your account admin.";

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  card: {
    background: '#111116', border: '1px solid #26262f',
    borderRadius: '10px', padding: '16px',
  } as React.CSSProperties,

  btn: (v: 'primary' | 'secondary' | 'danger' | 'ghost' = 'secondary'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '6px 12px', borderRadius: '6px', fontSize: '12.5px',
    fontWeight: 500, cursor: 'pointer', border: 'none',
    fontFamily: "'Inter',sans-serif", transition: 'all .15s',
    ...(v === 'primary'  ? { background: '#6366f1', color: '#fff' }
      : v === 'danger'   ? { background: 'rgba(244,63,94,.1)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' }
      : v === 'ghost'    ? { background: 'transparent', color: '#8e8ea8', border: '1px solid #26262f' }
      : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
  }),
};

const Spinner = () => (
  <svg style={{ animation: 'spin .8s linear infinite' }} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" />
  </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ success: boolean }> = ({ success }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '2px 8px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
    background: success ? 'rgba(34,197,94,.1)' : 'rgba(244,63,94,.1)',
    color: success ? '#22c55e' : '#f43f5e',
  }}>
    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'currentColor' }} />
    {success ? 'Connected' : 'Failed'}
  </span>
);

const RemarksPanel: React.FC<{ remarks: string[]; success: boolean }> = ({ remarks, success }) => {
  const [open, setOpen] = useState(!success); // auto-open on failure
  if (!remarks.length) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 8px', width: '100%', justifyContent: 'space-between' }}
      >
        <span>{success ? `✓ ${remarks.length} remark(s)` : `⚠ ${remarks.length} issue(s)`}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M1 3l4 4 4-4" />
        </svg>
      </button>
      {open && (
        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {remarks.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '11.5px', color: '#8e8ea8', padding: '5px 8px', background: '#17171e', borderRadius: '5px', border: '1px solid #26262f', lineHeight: 1.5 }}>
              <span style={{ color: success ? '#22c55e' : '#eab308', flexShrink: 0 }}>{success ? '✓' : '⚠'}</span>
              {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MissingPermissionsPanel: React.FC<{ missing: string[] }> = ({ missing }) => {
  const [open, setOpen] = useState(true);
  if (!missing.length) return null;
  return (
    <div style={{ marginTop: '6px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ ...S.btn('ghost'), fontSize: '11px', padding: '3px 8px', width: '100%', justifyContent: 'space-between', borderColor: 'rgba(244,63,94,.2)', color: '#f43f5e' }}
      >
        <span>✕ {missing.length} missing permission{missing.length > 1 ? 's' : ''}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M1 3l4 4 4-4" />
        </svg>
      </button>
      {open && (
        <div style={{ marginTop: '6px', background: '#17171e', border: '1px solid rgba(244,63,94,.15)', borderRadius: '6px', padding: '8px', maxHeight: '120px', overflowY: 'auto' }}>
          {missing.map(p => (
            <div key={p} style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: '#f43f5e', padding: '2px 0' }}>✕ {p}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── AWS Card ──────────────────────────────────────────────────────────────────

const AwsAccountCard: React.FC<{
  account: AwsAccountResult;
  onRemove: () => void;
  isRemoving: boolean;
  externalId: string;
}> = ({ account, onRemove, isRemoving, externalId }) => {
  const [showReconnect, setShowReconnect] = useState(false);

  return (
    <>
      <div style={{
        ...S.card,
        border: account.success ? '1px solid rgba(34,197,94,.15)' : '1px solid rgba(244,63,94,.2)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '12px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'rgba(255,153,0,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🟠</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>Amazon Web Services</div>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: '#52525f' }}>
              Account: {account.account_id}
            </div>
          </div>
          <StatusBadge success={account.success} />
        </div>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', color: '#52525f' }}>Region</span>
            <span style={{ fontSize: '11.5px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace" }}>{account.region}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', color: '#52525f' }}>Auth</span>
            <span style={{ fontSize: '11.5px', color: '#8e8ea8' }}>STS / IAM Role</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <span style={{ fontSize: '11.5px', color: '#52525f', flexShrink: 0 }}>Role ARN</span>
            <span style={{ fontSize: '10.5px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={account.role_arn}>
              {account.role_arn}
            </span>
          </div>
        </div>

        {/* Missing permissions + trust errors + remarks */}
        <MissingPermissionsPanel missing={account.missing_permissions} />
        {(account.trust_errors ?? []).length > 0 && (
          <RemarksPanel remarks={account.trust_errors!} success={false} />
        )}
        <RemarksPanel remarks={account.remarks} success={account.success} />

        {/* Actions */}
        <div style={{ display: 'flex', gap: '6px', paddingTop: '12px', marginTop: '10px', borderTop: '1px solid #26262f' }}>
          {!account.success && (
            <button
              style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center', fontSize: '11.5px', padding: '5px 8px' }}
              onClick={() => setShowReconnect(true)}
            >
              Fix & Re-connect →
            </button>
          )}
          <button
            style={{ ...S.btn('danger'), fontSize: '11.5px', padding: '5px 8px', opacity: isRemoving ? 0.6 : 1 }}
            onClick={onRemove}
            disabled={isRemoving}
          >
            {isRemoving ? '…' : 'Remove'}
          </button>
        </div>
      </div>

      {showReconnect && (
        <AWSSetupModal
          open={showReconnect}
          onClose={() => setShowReconnect(false)}
          onSuccess={() => setShowReconnect(false)}
          externalId={externalId}
        />
      )}
    </>
  );
};

// ── Azure Card ────────────────────────────────────────────────────────────────

const AzureAccountCard: React.FC<{
  account: AzureAccountResult;
  onRemove: () => void;
  isRemoving: boolean;
}> = ({ account, onRemove, isRemoving }) => {
  const [showReconnect, setShowReconnect] = useState(false);

  return (
    <>
      <div style={{
        ...S.card,
        border: account.success ? '1px solid rgba(34,197,94,.15)' : '1px solid rgba(244,63,94,.2)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '12px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'rgba(0,120,212,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🔷</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>Microsoft Azure</div>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: '#52525f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {account.subscription_id}
            </div>
          </div>
          <StatusBadge success={account.success} />
        </div>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '10px' }}>
          {([
            ['Tenant', account.tenant_id],
            ['Region', account.region],
            ['Auth', 'Service Principal / RBAC'],
          ] as [string, string][]).map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <span style={{ fontSize: '11.5px', color: '#52525f', flexShrink: 0 }}>{l}</span>
              <span style={{ fontSize: '10.5px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
            </div>
          ))}
          {account.granted_roles?.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11.5px', color: '#52525f', flexShrink: 0 }}>Roles</span>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {account.granted_roles.map(r => (
                  <span key={r} style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(34,197,94,.1)', color: '#22c55e', fontWeight: 600 }}>{r}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Missing + remarks */}
        <MissingPermissionsPanel missing={account.missing_permissions} />
        <RemarksPanel remarks={account.remarks} success={account.success} />

        {/* Actions */}
        <div style={{ display: 'flex', gap: '6px', paddingTop: '12px', marginTop: '10px', borderTop: '1px solid #26262f' }}>
          {!account.success && (
            <button
              style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center', fontSize: '11.5px', padding: '5px 8px' }}
              onClick={() => setShowReconnect(true)}
            >
              Fix & Re-connect →
            </button>
          )}
          <button
            style={{ ...S.btn('danger'), fontSize: '11.5px', padding: '5px 8px', opacity: isRemoving ? 0.6 : 1 }}
            onClick={onRemove}
            disabled={isRemoving}
          >
            {isRemoving ? '…' : 'Remove'}
          </button>
        </div>
      </div>

      {showReconnect && (
        <AzureSetupModal
          open={showReconnect}
          onClose={() => setShowReconnect(false)}
          onSuccess={() => setShowReconnect(false)}
        />
      )}
    </>
  );
};

// ── Provider selector ─────────────────────────────────────────────────────────

const ProviderSelectorModal: React.FC<{
  onSelect: (p: 'aws' | 'azure') => void;
  onClose: () => void;
}> = ({ onSelect, onClose }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
    onClick={onClose}
  >
    <div
      style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', padding: '26px', width: '420px', maxWidth: '95vw', boxShadow: '0 24px 60px rgba(0,0,0,.7)' }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5' }}>Connect Cloud Account</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#52525f' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2l10 10M12 2L2 12" /></svg>
        </button>
      </div>
      <div style={{ fontSize: '12.5px', color: '#8e8ea8', marginBottom: '20px' }}>Choose a cloud provider to connect</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {([
          { key: 'aws' as const, icon: '🟠', name: 'Amazon Web Services', desc: 'STS role assumption via IAM. Trust policy + permission policy. No long-lived credentials.' },
          { key: 'azure' as const, icon: '🔷', name: 'Microsoft Azure', desc: 'Service Principal with Contributor + User Access Administrator roles via consent flow.' },
        ]).map(p => (
          <div
            key={p.key}
            onClick={() => onSelect(p.key)}
            style={{ display: 'flex', gap: '12px', padding: '14px', borderRadius: '8px', border: '1px solid #26262f', cursor: 'pointer', transition: 'all .15s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = '#6366f1'; (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,.05)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = '#26262f'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <div style={{ fontSize: '24px', flexShrink: 0 }}>{p.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '3px' }}>{p.name}</div>
              <div style={{ fontSize: '12px', color: '#8e8ea8', lineHeight: 1.5 }}>{p.desc}</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#52525f" strokeWidth="1.5" style={{ flexShrink: 0, alignSelf: 'center' }}>
              <path d="M3 7h8M8 4l3 3-3 3" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Main view ─────────────────────────────────────────────────────────────────

const CloudAccountsView: React.FC = () => {
  const [externalId, setExternalId] = useState('');
  // Defaults to true so the button doesn't flash disabled before the dashboard call resolves.
  const [hasConnectionAccess, setHasConnectionAccess] = useState(true);

  // Verify API results
  const [awsData, setAwsData] = useState<AwsVerifyResponse | null>(null);
  const [azureData, setAzureData] = useState<AzureVerifyResponse | null>(null);

  // Loading states — separate for AWS and Azure so one failure doesn't block the other
  const [isLoadingAws, setIsLoadingAws] = useState(true);
  const [isLoadingAzure, setIsLoadingAzure] = useState(true);

  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [showProviderSelector, setShowProviderSelector] = useState(false);

  // ── Fetch dashboard for external_id + connection access ──────────────────
  useEffect(() => {
    axiosInstance.get('/users/dashboard')
      .then(res => {
        if (res.data?.external_id) setExternalId(res.data.external_id);
        if (typeof res.data?.has_connection_access === 'boolean') {
          setHasConnectionAccess(res.data.has_connection_access);
        }
      })
      .catch(() => {}); // non-blocking, externalId/access just stay at defaults
  }, []);

  // ── Fetch verify APIs independently ───────────────────────────────────────
  const fetchAwsAccounts = useCallback(async () => {
    setIsLoadingAws(true);
    try {
      const res = await axiosInstance.post('/aws/verify');
      setAwsData(res.data);
    } catch {
      setAwsData(null);
    } finally {
      setIsLoadingAws(false);
    }
  }, []);

  const fetchAzureAccounts = useCallback(async () => {
    setIsLoadingAzure(true);
    try {
      const res = await axiosInstance.post('/azure/verify');
      setAzureData(res.data);
    } catch {
      setAzureData(null);
    } finally {
      setIsLoadingAzure(false);
    }
  }, []);

  useEffect(() => {
    fetchAwsAccounts();
    fetchAzureAccounts();
  }, [fetchAwsAccounts, fetchAzureAccounts]);

  // ── Remove ────────────────────────────────────────────────────────────────
  const handleRemoveAws = async (roleArn: string) => {
    setRemovingKey(roleArn);
    try {
      await axiosInstance.delete('/aws/client/', { data: { role_arn: roleArn } });
      // Remove from local state — no need to re-call verify
      setAwsData(prev => prev
        ? { ...prev, accounts: prev.accounts.filter(a => a.role_arn !== roleArn) }
        : prev
      );
    } catch (err) {
      console.error('Failed to remove AWS account:', err);
    } finally {
      setRemovingKey(null);
    }
  };

  const handleRemoveAzure = async (subscriptionId: string) => {
    setRemovingKey(subscriptionId);
    try {
      await axiosInstance.delete('/azure/client/', { data: { subscription_id: subscriptionId } });
      setAzureData(prev => prev
        ? { ...prev, accounts: prev.accounts.filter(a => a.subscription_id !== subscriptionId) }
        : prev
      );
    } catch (err) {
      console.error('Failed to remove Azure account:', err);
    } finally {
      setRemovingKey(null);
    }
  };

  const handleModalSuccess = () => {
    setActiveModal(null);
    // Re-fetch both verify APIs after a new account is connected
    fetchAwsAccounts();
    fetchAzureAccounts();
  };

  const handleOpenProviderSelector = () => {
    if (!hasConnectionAccess) return;
    setShowProviderSelector(true);
  };

  const awsAccounts = awsData?.accounts ?? [];
  const azureAccounts = azureData?.accounts ?? [];
  const totalAccounts = awsAccounts.length + azureAccounts.length;
  const isLoading = isLoadingAws && isLoadingAzure;

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Top bar */}
      <div style={{ height: '50px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span>Home</span><span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>Cloud Accounts</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button
            style={S.btn('secondary')}
            onClick={() => { fetchAwsAccounts(); fetchAzureAccounts(); }}
            disabled={isLoadingAws || isLoadingAzure}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
              style={{ animation: (isLoadingAws || isLoadingAzure) ? 'spin .8s linear infinite' : 'none' }}>
              <path d="M10 6A4 4 0 112 6" /><path d="M10 2v4H6" />
            </svg>
            {(isLoadingAws || isLoadingAzure) ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            style={{
              ...S.btn('primary'),
              ...(hasConnectionAccess ? {} : { opacity: 0.45, cursor: 'not-allowed' }),
            }}
            onClick={handleOpenProviderSelector}
            disabled={!hasConnectionAccess}
            title={hasConnectionAccess ? undefined : NO_ACCESS_TOOLTIP}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="currentColor"><path d="M5.5 1v9M1 5.5h9" /></svg>
            Connect Account
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 24px', maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>Cloud Accounts</h1>
          <p style={{ fontSize: '13px', color: '#8e8ea8' }}>Connect AWS and Azure accounts. Deploy to any of them from a single workspace.</p>
        </div>

        {/* Both still loading */}
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '12px' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }} />
            <span style={{ fontSize: '13px', color: '#52525f' }}>Loading accounts…</span>
          </div>
        ) : totalAccounts === 0 ? (
          /* Empty state */
          <div style={{ background: '#111116', border: '1px dashed #32323d', borderRadius: '12px', padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '14px' }}>☁️</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '6px' }}>No cloud accounts connected</div>
            <div style={{ fontSize: '13px', color: '#8e8ea8', marginBottom: '20px' }}>Connect your first AWS or Azure account to start deploying</div>
            <button
              style={{
                ...S.btn('primary'),
                ...(hasConnectionAccess ? {} : { opacity: 0.45, cursor: 'not-allowed' }),
              }}
              onClick={handleOpenProviderSelector}
              disabled={!hasConnectionAccess}
              title={hasConnectionAccess ? undefined : NO_ACCESS_TOOLTIP}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="currentColor"><path d="M5.5 1v9M1 5.5h9" /></svg>
              Connect Account
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            {/* AWS section */}
            {isLoadingAws ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', background: '#111116', borderRadius: '10px', border: '1px solid #26262f' }}>
                <Spinner /><span style={{ fontSize: '13px', color: '#52525f' }}>Loading AWS accounts…</span>
              </div>
            ) : awsAccounts.length > 0 ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '16px' }}>🟠</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#eeeef5' }}>AWS Accounts</span>
                  <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '20px', background: '#1e1e27', color: '#52525f', border: '1px solid #32323d' }}>
                    {awsAccounts.length}
                  </span>
                  {awsData && (
                    <span style={{ fontSize: '10.5px', color: '#52525f', marginLeft: '4px' }}>
                      · verified {new Date(awsData.verified_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                  {awsAccounts.map(acc => (
                    <AwsAccountCard
                      key={acc.role_arn}
                      account={acc}
                      onRemove={() => handleRemoveAws(acc.role_arn)}
                      isRemoving={removingKey === acc.role_arn}
                      externalId={externalId}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {/* Azure section */}
            {isLoadingAzure ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', background: '#111116', borderRadius: '10px', border: '1px solid #26262f' }}>
                <Spinner /><span style={{ fontSize: '13px', color: '#52525f' }}>Loading Azure accounts…</span>
              </div>
            ) : azureAccounts.length > 0 ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '16px' }}>🔷</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#eeeef5' }}>Azure Accounts</span>
                  <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '20px', background: '#1e1e27', color: '#52525f', border: '1px solid #32323d' }}>
                    {azureAccounts.length}
                  </span>
                  {azureData && (
                    <span style={{ fontSize: '10.5px', color: '#52525f', marginLeft: '4px' }}>
                      · verified {new Date(azureData.verified_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                  {azureAccounts.map(acc => (
                    <AzureAccountCard
                      key={acc.subscription_id}
                      account={acc}
                      onRemove={() => handleRemoveAzure(acc.subscription_id)}
                      isRemoving={removingKey === acc.subscription_id}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {/* Add more card */}
            <div
              onClick={handleOpenProviderSelector}
              title={hasConnectionAccess ? undefined : NO_ACCESS_TOOLTIP}
              style={{
                border: '1px dashed #32323d', borderRadius: '10px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '10px',
                cursor: hasConnectionAccess ? 'pointer' : 'not-allowed',
                padding: '20px', transition: 'all .15s',
                opacity: hasConnectionAccess ? 1 : 0.5,
              }}
              onMouseEnter={e => {
                if (!hasConnectionAccess) return;
                (e.currentTarget as HTMLElement).style.borderColor = '#6366f1';
                (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,.05)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = '#32323d';
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#1e1e27', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#52525f" strokeWidth="1.5"><path d="M6.5 1.5v10M1.5 6.5h10" /></svg>
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#8e8ea8' }}>Connect another account</div>
                <div style={{ fontSize: '11.5px', color: '#52525f' }}>
                  {hasConnectionAccess ? 'AWS · Azure' : 'No permission to add connections'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showProviderSelector && (
        <ProviderSelectorModal
          onSelect={p => { setShowProviderSelector(false); setActiveModal(p); }}
          onClose={() => setShowProviderSelector(false)}
        />
      )}
      {activeModal === 'aws' && (
        <AWSSetupModal
          open
          onClose={() => setActiveModal(null)}
          onSuccess={handleModalSuccess}
          externalId={externalId}
        />
      )}
      {activeModal === 'azure' && (
        <AzureSetupModal
          open
          onClose={() => setActiveModal(null)}
          onSuccess={handleModalSuccess}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CloudAccountsView;